import Joi from 'joi';
import getConfig from 'next/config';
import { addCleanCache } from '@culture/common-helpers/params';
import { addEntitiesMicrodata } from '@culture/common-helpers/microdata';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import _ from 'underscore';
import Cookies from 'cookies';
import { getCompilations } from 'utils/helpers/liveCompilations';
import LiveTemplate from 'pageTemplates/main';
import {
	collectResultIdsInCookies,
	getEntityIdsFromCookies,
	getRecommendationsRequest,
	getRecommendationsResultsFromCookies
} from 'utils/helpers/recommendations';

const { serverRuntimeConfig } = getConfig();
const recommendationPageEntityName = 'live/broadcasts';

const Live = (props) => <LiveTemplate {...props} />;

const getRubricPage = (cleanCache) =>
	apiRequest('live/rubricPage', addCleanCache({}, cleanCache));

const getStreamingPlatforms = (cleanCache) =>
	apiRequest(
		'streamingPlatforms',
		addCleanCache(
			{
				groupByType: true,
				statuses: ['published'],
				sort: '-broadcastCount',
				fields: ['thumbnailFile', 'name', 'title', 'locale'].join(),
				limit: 10,
				cached: true
			},
			cleanCache
		)
	);

const getBroadcast = (cleanCache) =>
	apiRequest(
		'broadcast',
		addCleanCache(
			{
				statuses: 'published',
				isPassed: false,
				limit: 8,
				sort: 'publishDate',
				expand: ['institute', 'materials'],
				cached: true,
				isWasBroadcast: true
			},
			cleanCache
		)
	);

const getRecommendedMovies = (_ids, cleanCache) => {
	const params = addCleanCache(
		{
			_ids,
			sort: '-views'
		},
		cleanCache
	);

	return apiRequest('movies', params);
};

const getRecommendations = async (cookies, { cleanCache, rubricId }) => {
	const cachedRecommendationResultIds = getRecommendationsResultsFromCookies(
		cookies,
		recommendationPageEntityName
	);

	if (!_.isEmpty(cachedRecommendationResultIds)) {
		const movies = await getRecommendedMovies(
			cachedRecommendationResultIds,
			cleanCache
		);
		return movies?.items || [];
	}
	const visitedEntitiesIds = getEntityIdsFromCookies('broadcasts', cookies);

	const recommendations = await getRecommendationsRequest({
		entityType: 'movies',
		entityIds: visitedEntitiesIds,
		rubricId
	});

	if (!_.isEmpty(recommendations)) {
		const recommendationEntitiesIds = recommendations.map((item) => item._id);
		collectResultIdsInCookies(
			cookies,
			recommendationPageEntityName,
			recommendationEntitiesIds
		);
	}
	return recommendations;
};

export async function getServerSideProps({ query, req, res }) {
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false)
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const cookies = new Cookies(req, res);

	const [
		{ blocks, rubric },
		{ items: streamingPlatforms },
		broadcast
	] = await batchApiRequest([
		getRubricPage(params.cleanCache),
		getStreamingPlatforms(params.cleanCache),
		getBroadcast(params.cleanCache)
	]);

	const recommendations = await getRecommendations(cookies, {
		cleanCache: params?.cleanCache,
		rubricId: rubric?._id
	});

	const compilations = getCompilations(blocks);

	res.setHeader('Cache-Control', serverRuntimeConfig.cacheControl.smallAge);

	return {
		props: {
			hotBroadcast: addEntitiesMicrodata(blocks.hotBroadcast, 'broadcast'),
			incutPromoBlock: blocks.incutPromoBlock,
			bottomPromoBlock: blocks.bottomPromoBlock,
			reviewsPublications: blocks.reviewsPublications,
			reviewsPromoBlocks: blocks.reviewsPromoBlocks,
			rubric,
			compilations,
			streamingPlatforms,
			broadcasts: addEntitiesMicrodata(broadcast.items, 'broadcast'),
			recommendations: recommendations || null
		}
	};
}

export default Live;
